// AI views: AIImportView, ValidationView, ExtractionDashView,
// RedlineReview, DraftingView.
//
// AIImportView is the bulk PDF→contract import flow with batch
// processing, AI field extraction, parent matching, and dedup checks.
// ValidationView is the human review queue for AI-extracted fields.
// ExtractionDashView shows extraction stats and quality metrics.
// RedlineReview displays AI-suggested redlines from playbook analysis.
// DraftingView is the contract drafting assistant with chat + tools.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { diffWords } from "diff";
import { T } from "../theme";
import { fmtDate, toTitleCase } from "../utils";
import { apiCall } from "../apiClient";
import { AI_UNIVERSAL_FIELDS } from "../constants";
import { renderMarkdown } from "./markdown";
import { PdfHighlightModal } from "./PdfHighlightModal";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";

// Validate an extraction row before allowing import. Blocks approval when:
// - A field has a value with low confidence (AI extracted something but is uncertain)
// - A field value contains placeholder markers ([brackets], "insert here",
//   "TBD", "to be determined", etc.) — the AI hallucinated a placeholder
// - A field is empty but the AI is confident it SHOULD be present (rare)
//
// Does NOT block on: empty field + low confidence. That's the AI saying the
// field is not applicable / not in this document — a normal case (e.g., an MSA
// doesn't have "Amendment Effective Date" or "Sub-processor Requirements").
// Returns { ok, lowConf: string[], missing: string[], placeholder: string[] }.
const PLACEHOLDER_RE = /[\[\]<>{}]|insert\s|tbd|to\s+be\s+determined|placeholder|\bn\/?a\b/i;
const validateImportRow = (row: any): { ok: boolean; lowConf: string[]; missing: string[]; placeholder: string[]; blocking: string[] } => {
  const lowConf: string[] = [];
  const missing: string[] = [];
  const placeholder: string[] = [];
  Object.entries(row).forEach(([k, v]: [string, any]) => {
    if (k.startsWith('__') || k === 'suggestedRelationship') return;
    if (!v || typeof v !== 'object') return;
    const val = (v.v == null ? '' : String(v.v)).trim();
    const conf = (v.conf || v.confidence || 'medium').toLowerCase();
    if (!val) { if (conf !== 'low') missing.push(k); return; }
    if (PLACEHOLDER_RE.test(val)) { placeholder.push(k); return; }
    if (conf === 'low') { lowConf.push(k); }
  });
  const blocking = [...missing, ...placeholder, ...lowConf];
  return { ok: blocking.length === 0, lowConf, missing, placeholder, blocking };
};


/* ═══════════════════ EXTRACTION DASHBOARD ═══════════════════ */
export const ExtractionDashView = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [reprofiling, setReprofiling] = useState<any>(null);
  const [reprofileResults, setReprofileResults] = useState<Record<string, any>>({});
  const [reprofileAllRunning, setReprofileAllRunning] = useState(false);
  const [reprofileAllProgress, setReprofileAllProgress] = useState<Record<string, any>>({ current: 0, total: 0, currentName: "" });
  const [reprofileAllResults, setReprofileAllResults] = useState<any>(null);

  const loadData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    const [statsData, candData] = await Promise.all([
      apiCall("/extraction/stats"),
      apiCall("/extraction/reprofile/candidates"),
    ]);
    if (statsData) setStats(statsData);
    if (candData) setCandidates(candData.candidates || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(true); }, [loadData]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => loadData(false), 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const reprofileSingle = async (contractId: string) => {
    setReprofiling(contractId);
    setReprofileResults((prev: any) => ({ ...prev, [contractId]: null }));
    const result = await apiCall(`/extraction/reprofile/${contractId}`, { method: "POST" });
    if (result && !result.error) {
      setReprofileResults((prev: any) => ({ ...prev, [contractId]: result }));
    } else {
      setReprofileResults((prev: any) => ({ ...prev, [contractId]: { error: result?.error || "Failed" } }));
    }
    setReprofiling(null);
    return result;
  };

  const reprofileAllCandidates = async () => {
    setReprofileAllRunning(true);
    for (const c of candidates) {
      if (reprofileResults[c.id]?.updated !== undefined) continue;
      await reprofileSingle(c.id);
    }
    setReprofileAllRunning(false);
    loadData();
  };

  const reprofileAll = async () => {
    setReprofileAllRunning(true);
    setReprofileAllResults(null);
    try {
      const contracts = await apiCall("/contracts");
      const allContracts = contracts?.flat || [];
      if (allContracts.length === 0) { setReprofileAllRunning(false); return; }
      setReprofileAllProgress({ current: 0, total: allContracts.length, currentName: "" });
      let succeeded = 0, failed = 0;
      for (let i = 0; i < allContracts.length; i++) {
        const c = allContracts[i];
        setReprofileAllProgress({ current: i + 1, total: allContracts.length, currentName: c.name || c.counterparty_entity || "Contract" });
        try {
          const res = await apiCall(`/extraction/reprofile/${c.id}`, { method: "POST" });
          if (res?.updated !== undefined) succeeded++; else failed++;
        } catch { failed++; }
        if (i < allContracts.length - 1) await new Promise(r => setTimeout(r, 3000));
      }
      setReprofileAllResults({ succeeded, failed, total: allContracts.length });
    } catch (err: any) { console.error("Re-profile all failed:", err); }
    setReprofileAllRunning(false);
    loadData();
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.txM }}>Loading extraction analytics...</div>;
  if (!stats) return <div style={{ padding: 40, textAlign: "center", color: T.red }}>Failed to load stats.</div>;

  const confMap: Record<string, any> = {};
  (stats.by_confidence || []).forEach((r: any) => { confMap[r.confidence] = r.count; });
  const highCount = confMap["high"] || 0;
  const medCount = confMap["medium"] || 0;
  const lowCount = confMap["low"] || 0;
  const total = stats.total_fields || 1;
  const highPct = ((highCount / total) * 100).toFixed(1);
  const lowPct = ((lowCount / total) * 100).toFixed(1);

  const valMap: Record<string, any> = {};
  (stats.by_validation || []).forEach((r: any) => { valMap[r.validation_status] = r.count; });
  const editedCount = valMap["edited"] || 0;

  const ConfBar = ({ high, medium, low, total: t }: { high?: number; medium?: number; low?: number; total?: number }) => {
    if (!t) return <div style={{ height: 8, backgroundColor: T.bg4, borderRadius: 4 }} />;
    const hp = ((high || 0) / t) * 100, mp = ((medium || 0) / t) * 100, lp = ((low || 0) / t) * 100;
    return (
      <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", backgroundColor: T.bg4 }}>
        {hp > 0 && <div style={{ width: `${hp}%`, backgroundColor: T.grn }} />}
        {mp > 0 && <div style={{ width: `${mp}%`, backgroundColor: T.ylw }} />}
        {lp > 0 && <div style={{ width: `${lp}%`, backgroundColor: T.red }} />}
      </div>
    );
  };

  return (
    <div style={{ flex: 1, overflow: "auto", padding: "32px 40px" }}>
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: T.tx, fontFamily: "'Source Serif 4', serif" }}>AI Extraction Accuracy</div>
          <div style={{ fontSize: 12, color: T.txM, marginTop: 4 }}>Confidence distribution and validation metrics across all extracted fields</div>
        </div>
        <RefreshBtn onClick={() => loadData(false)} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 28 }}>
        {[
          { label: "Total Fields", value: total, color: T.tx },
          { label: "High Confidence", value: `${highPct}%`, sub: `${highCount} fields`, color: T.grn },
          { label: "Low Confidence", value: `${lowPct}%`, sub: `${lowCount} fields`, color: T.red },
          { label: "Edited / Corrected", value: editedCount, color: T.orn },
        ].map((s: any, i: number) => (
          <div key={i} style={{ backgroundColor: T.bg2, borderRadius: 10, border: `1px solid ${T.brd}`, padding: "20px 22px" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 }}>{s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
            {s.sub && <div style={{ fontSize: 11, color: T.txM, marginTop: 2 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28 }}>
        <div style={{ backgroundColor: T.bg2, borderRadius: 10, border: `1px solid ${T.brd}`, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 16 }}>Confidence by Contract Type</div>
          {(stats.confidence_by_type || []).length === 0 && <div style={{ fontSize: 12, color: T.txD }}>No data yet</div>}
          {(stats.confidence_by_type || []).map((t2: any, i: number) => (
            <div key={i} style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: T.tx }}>{t2.contract_type || "Unknown"}</span>
                <span style={{ fontSize: 11, color: T.txD }}>{t2.total} fields</span>
              </div>
              <ConfBar high={t2.high_count} medium={t2.medium_count} low={t2.low_count} total={t2.total} />
              <div style={{ display: "flex", gap: 12, marginTop: 3 }}>
                <span style={{ fontSize: 10, color: T.grn }}>{t2.high_count} high</span>
                <span style={{ fontSize: 10, color: T.ylw }}>{t2.medium_count} med</span>
                <span style={{ fontSize: 10, color: T.red }}>{t2.low_count} low</span>
              </div>
            </div>
          ))}
          <div style={{ display: "flex", gap: 16, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.brdSubtle}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: T.grn }} /><span style={{ fontSize: 10, color: T.txD }}>High</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: T.ylw }} /><span style={{ fontSize: 10, color: T.txD }}>Medium</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: T.red }} /><span style={{ fontSize: 10, color: T.txD }}>Low</span></div>
          </div>
        </div>

        <div style={{ backgroundColor: T.bg2, borderRadius: 10, border: `1px solid ${T.brd}`, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 16 }}>Fields Needing Attention</div>
          {(stats.low_confidence_fields || []).length === 0 && (stats.edited_fields || []).length === 0 && <div style={{ fontSize: 12, color: T.txD }}>No issues found</div>}
          {(stats.low_confidence_fields || []).length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.red, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6 }}>Most Often Low Confidence</div>
              {stats.low_confidence_fields.map((f: any, i: number) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
                  <span style={{ fontSize: 12, color: T.tx }}>{f.field_name}</span>
                  <span style={{ fontSize: 11, color: T.red, fontWeight: 600 }}>{f.count}x</span>
                </div>
              ))}
            </>
          )}
          {(stats.edited_fields || []).length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.orn, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6, marginTop: 14 }}>Most Often Edited</div>
              {stats.edited_fields.map((f: any, i: number) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
                  <span style={{ fontSize: 12, color: T.tx }}>{f.field_name}</span>
                  <span style={{ fontSize: 11, color: T.orn, fontWeight: 600 }}>{f.count}x</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28 }}>
        <div style={{ backgroundColor: T.bg2, borderRadius: 10, border: `1px solid ${T.brd}`, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 16 }}>Recent Extractions</div>
          {(stats.recent_extractions || []).length === 0 && <div style={{ fontSize: 12, color: T.txD }}>No extractions yet</div>}
          {(stats.recent_extractions || []).map((c: any, i: number) => (
            <div key={i} style={{ padding: "10px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, marginBottom: 3 }}>{c.name}</div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ fontSize: 10, color: T.txD }}>{c.contract_type}</span>
                <span style={{ fontSize: 10, color: T.txD }}>{c.field_count} fields</span>
                <span style={{ fontSize: 10, color: T.grn, fontWeight: 600 }}>{c.high_count} high</span>
                {c.low_count > 0 && <span style={{ fontSize: 10, color: T.red, fontWeight: 600 }}>{c.low_count} low</span>}
              </div>
              {c.ai_extraction_date && <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>{fmtDate(c.ai_extraction_date)}</div>}
            </div>
          ))}
        </div>

        <div style={{ backgroundColor: T.bg2, borderRadius: 10, border: `1px solid ${T.brd}`, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 16 }}>Contracts Needing Review</div>
          {(stats.problem_contracts || []).length === 0 && <div style={{ fontSize: 12, color: T.txD }}>No contracts flagged</div>}
          {(stats.problem_contracts || []).map((c: any, i: number) => (
            <div key={i} style={{ padding: "10px 0", borderBottom: `1px solid ${T.brdSubtle}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.tx }}>{c.name}</div>
                <div style={{ fontSize: 10, color: T.txD }}>{c.contract_type}</div>
              </div>
              <div style={{ backgroundColor: T.redS, color: T.red, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 12 }}>{c.low_count} low</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ backgroundColor: T.bg2, borderRadius: 10, border: `1px solid ${T.brd}`, padding: "20px 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.tx }}>Re-Profile Contracts</div>
            <div style={{ fontSize: 11, color: T.txM, marginTop: 2 }}>Re-extract metadata from contracts using the latest AI prompts. Validated fields are preserved.</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {candidates.length > 0 && (
              <button onClick={() => { reprofileAllCandidates(); }} disabled={reprofileAllRunning || reprofiling} style={{ padding: "8px 18px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg2, color: reprofileAllRunning ? T.txD : T.acc, fontSize: 12, fontWeight: 600, cursor: reprofileAllRunning ? "default" : "pointer", opacity: reprofiling ? 0.5 : 1 }}>
                {`Flagged (${candidates.length})`}
              </button>
            )}
            <Btn v="primary" sz="sm" onClick={() => { reprofileAll(); }} disabled={reprofileAllRunning || reprofiling}>
              {reprofileAllRunning ? `Re-Profiling ${reprofileAllProgress.current} of ${reprofileAllProgress.total}...` : "Re-Profile All Contracts"}
            </Btn>
          </div>
        </div>
        {reprofileAllRunning && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.tx }}>{reprofileAllProgress.currentName}</span>
              <span style={{ fontSize: 11, color: T.txM }}>{reprofileAllProgress.current} of {reprofileAllProgress.total}</span>
            </div>
            <div style={{ height: 4, borderRadius: 2, backgroundColor: T.bg4, overflow: "hidden" }}>
              <div style={{ width: `${Math.round((reprofileAllProgress.current / reprofileAllProgress.total) * 100)}%`, height: "100%", backgroundColor: T.acc, transition: "width .3s", borderRadius: 2 }} />
            </div>
            <div style={{ fontSize: 10, color: T.txD, marginTop: 4 }}>Contracts are re-profiled sequentially with rate limit pacing. This may take several minutes.</div>
          </div>
        )}
        {reprofileAllResults && (
          <div style={{ padding: 10, backgroundColor: T.grnS, borderRadius: 4, marginBottom: 12, fontSize: 11, color: T.grn }}>
            Re-profiling complete: {reprofileAllResults.succeeded} succeeded, {reprofileAllResults.failed} failed out of {reprofileAllResults.total} contracts.
          </div>
        )}
        {candidates.length === 0 && !reprofileAllRunning && !reprofileAllResults && <div style={{ fontSize: 12, color: T.txD, textAlign: "center", padding: 20 }}>No contracts flagged for re-profiling</div>}
        {candidates.map((c: any, i: number) => {
          const result = reprofileResults[c.id];
          const isActive = reprofiling === c.id;
          return (
            <div key={c.id} style={{ padding: "12px 0", borderBottom: i < candidates.length - 1 ? `1px solid ${T.brdSubtle}` : "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.tx }}>{c.name}</div>
                <div style={{ display: "flex", gap: 10, marginTop: 3 }}>
                  <span style={{ fontSize: 10, color: T.txD }}>{c.contract_type || "Unknown"}</span>
                  <span style={{ fontSize: 10, color: T.txD }}>{c.total_fields} fields</span>
                  {c.low_count > 0 && <span style={{ fontSize: 10, color: T.red, fontWeight: 600 }}>{c.low_count} low</span>}
                  {c.validated_count > 0 && <span style={{ fontSize: 10, color: T.grn, fontWeight: 600 }}>{c.validated_count} validated</span>}
                  {c.ai_extraction_date && <span style={{ fontSize: 10, color: T.txD }}>Last: {fmtDate(c.ai_extraction_date)}</span>}
                  {!c.ai_extraction_date && c.total_fields === 0 && <span style={{ fontSize: 10, color: T.orn, fontWeight: 600 }}>Never extracted</span>}
                </div>
                {result && !result.error && (
                  <div style={{ fontSize: 10, color: T.grn, fontWeight: 600, marginTop: 4 }}>{result.updated} updated, {result.skipped} preserved</div>
                )}
                {result?.error && (
                  <div style={{ fontSize: 10, color: T.red, marginTop: 4 }}>{result.error}</div>
                )}
              </div>
              <button onClick={() => reprofileSingle(c.id)} disabled={isActive || reprofileAllRunning} style={{ padding: "6px 14px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: isActive ? T.bg4 : T.bg2, color: isActive ? T.txD : T.acc, fontSize: 11, fontWeight: 600, cursor: isActive ? "default" : "pointer", whiteSpace: "nowrap", marginLeft: 12 }}>
                {isActive ? "Re-profiling..." : result && !result.error ? "Done" : "Re-Profile"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
